import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertTriangle, Clock, CreditCard } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';

const SubscriptionBanner = () => {
  const navigate = useNavigate();
  const { subscription, status } = useSubscription();
  const [dismissed, setDismissed] = useState(false);

  const bannerConfig = useMemo(() => {
    if (!subscription) return null;

    const now = new Date();

    // Trial
    if (status === 'trial' || status === 'trialing') {
      const trialEnd = subscription.trial_end || subscription.trialEnd;
      if (!trialEnd) return null;
      const endDate = new Date(trialEnd);
      const daysRemaining = Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
      return {
        type: 'trial',
        bg: 'bg-amber-50 border-amber-200',
        textColor: 'text-amber-800',
        icon: Clock,
        iconColor: 'text-amber-600',
        message: `تبقى ${daysRemaining} أيام على انتهاء تجربتك المجانية.`,
        actionText: 'رقّي الآن!',
        actionPath: '/settings/subscription/upgrade',
      };
    }

    // Past due
    if (status === 'past_due') {
      return {
        type: 'past_due',
        bg: 'bg-red-50 border-red-200',
        textColor: 'text-red-800',
        icon: AlertTriangle,
        iconColor: 'text-red-600',
        message: 'اشتراكك متأخر بالدفع. يرجى التواصل معنا',
        actionText: null,
        actionPath: null,
      };
    }

    // Expiring soon (active but < 7 days remaining)
    if (status === 'active') {
      const endDate = new Date(subscription.end_date || subscription.endDate || '');
      if (!isNaN(endDate.getTime())) {
        const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        if (daysRemaining > 0 && daysRemaining <= 7) {
          return {
            type: 'expiring',
            bg: 'bg-amber-50 border-amber-200',
            textColor: 'text-amber-800',
            icon: CreditCard,
            iconColor: 'text-amber-600',
            message: `اشتراكك ينتهي خلال ${daysRemaining} أيام`,
            actionText: 'تجديد',
            actionPath: '/settings/subscription/upgrade',
          };
        }
      }
      // Active and not expiring soon - don't show
      return null;
    }

    return null;
  }, [subscription, status]);

  if (dismissed || !bannerConfig) return null;

  const Icon = bannerConfig.icon;

  return (
    <div className={`w-full px-4 py-2.5 border-b ${bannerConfig.bg} flex items-center justify-between`} dir="rtl">
      <div className="flex items-center gap-2 flex-1">
        <Icon className={`w-4 h-4 flex-shrink-0 ${bannerConfig.iconColor}`} />
        <span className={`text-sm font-medium ${bannerConfig.textColor}`}>
          {bannerConfig.message}
        </span>
        {bannerConfig.actionText && (
          <button
            onClick={() => navigate(bannerConfig.actionPath)}
            className={`text-sm font-bold underline underline-offset-2 ${bannerConfig.textColor} hover:opacity-80 transition-opacity mr-1`}
          >
            {bannerConfig.actionText}
          </button>
        )}
      </div>
      <button
        onClick={() => setDismissed(true)}
        className={`p-1 rounded hover:bg-black/5 transition-colors ${bannerConfig.textColor}`}
        title="إغلاق"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default SubscriptionBanner;
